// Ported from dev-scrips-pm-react src/billing/store/print-preview-store.ts (PROD-1119 cross-module closure).
import { create } from 'zustand';

/**
 * UI state for the shared billing print-preview screen (PROD-1001).
 *
 * Flutter's claim-popup / invoice Print does NOT open a browser tab — it
 * fetches the PDF and shows it in an in-app preview screen
 * (`PrintInvoiceState` / `PrintReceiptState` → PDF viewer). We mirror that
 * with a single mounted `<PrintPreviewDialog />` driven by this store, so any
 * caller (`usePrintInvoice` / `usePrintReceipt`, the claim payment rows, the
 * invoice header) just calls `open(url)` and the preview screen appears.
 *
 * Zustand owns this (pure UI state) per the project state rule — never the PDF
 * URL itself as *server* state; the URL is a transient, already-resolved value
 * handed in by the print mutation.
 */
interface PrintPreviewState {
  /** Resolved PDF URL (Azure Blob SAS link) currently shown, or null when closed. */
  url: string | null;
  /** Dialog header label, e.g. "Invoice" / "Receipt". */
  title: string;
  /**
   * Best-effort: attempt `iframe.contentWindow.print()` once the PDF loads.
   * Cross-origin SAS URLs block programmatic print, so this is a hint, not a
   * guarantee — the embedded PDF viewer's own print control + the dialog's
   * Print button are the reliable paths. `true` for the "Print" affordance,
   * `false` for "View Receipt".
   */
  autoPrint: boolean;
  /**
   * true when `url` is a `blob:` object URL this store OWNS (minted from base64
   * bytes by `resolvePdfResponseToUrl`) and must revoke. Its lifetime follows
   * the preview: it is revoked on `close()` or when a new url replaces it — NOT
   * on a fixed timer, so "Open in new tab" keeps working for as long as the
   * dialog stays open (PROD-1001 review, P2). SAS-url upgrades minted inside the
   * dialog are a separate blob the dialog revokes itself.
   */
  ownsBlob: boolean;
  /**
   * Monotonic "open generation" — bumped on every `open()` call, including a
   * reopen with the SAME url. `PrintPreviewDialog` is a single long-lived
   * mounted instance (it never unmounts between opens), so its own SAS→blob
   * upgrade cache (`sasLoad`, keyed by `forUrl`) can otherwise survive a
   * close+reopen: if the backend returns the identical SAS url on a later
   * print, `forUrl === url` still matches the OLD (already-revoked-on-close)
   * cache entry, so the dialog would assign a revoked blob to the iframe
   * while the fresh fetch is still in flight (Codex v6 P2). Because `open()`
   * sets `url` and bumps `generation` in the SAME `set()` call, the dialog's
   * subscription re-render always sees the new url and the new generation
   * together — keying the SAS cache on `(forUrl, forGeneration)` instead of
   * `forUrl` alone means a reopen is always treated as a fresh load, never a
   * cache hit, even when the url string is unchanged.
   */
  generation: number;
  open: (
    url: string,
    opts?: { title?: string; autoPrint?: boolean; ownsBlob?: boolean },
  ) => void;
  close: () => void;
}

/** Revoke a store-owned blob: url if one is currently held. */
function revokeOwned(state: { url: string | null; ownsBlob: boolean }) {
  if (state.ownsBlob && state.url) URL.revokeObjectURL(state.url);
}

export const usePrintPreviewStore = create<PrintPreviewState>((set, get) => ({
  url: null,
  title: 'Print Preview',
  autoPrint: false,
  ownsBlob: false,
  generation: 0,
  open: (url, opts) => {
    // Replacing an open preview: revoke the prior owned blob before swapping so
    // we never leak a same-origin PHI object URL across invoices.
    revokeOwned(get());
    set({
      url,
      title: opts?.title ?? 'Print Preview',
      autoPrint: opts?.autoPrint ?? false,
      ownsBlob: opts?.ownsBlob ?? false,
      // Every open() — even a reopen of the identical url — is a fresh load.
      generation: get().generation + 1,
    });
  },
  close: () => {
    // Blob lifetime = preview lifetime: revoke on close, not on a timer.
    revokeOwned(get());
    set({ url: null, autoPrint: false, ownsBlob: false });
  },
}));
